/**
 * Een gekozen bestand klaarmaken om naar de server te sturen.
 *
 * Een foto van een moderne telefoon is 4000 pixels breed en een paar MB groot.
 * Voor het lezen van een menukaart is dat zonde: het model kijkt toch naar een
 * kleinere versie, en het versturen kost de kraam op een festivalterrein zijn
 * hele 4G-verbinding. Daarom gaat een foto eerst door een canvas.
 *
 * Een PDF blijft zoals hij is — die is meestal al klein en bevat de tekst zelf.
 */

/** Meer dan dit heeft het model niet nodig om een kaart te lezen. */
const MAXIMALE_ZIJDE = 1568;

export interface Klaargemaakt {
  /** Het bestand als base64, zonder het `data:`-voorvoegsel. */
  data: string;
  mediaType: string;
}

function naarBase64(bestand: Blob): Promise<string> {
  return new Promise((klaar, mislukt) => {
    const lezer = new FileReader();
    lezer.onerror = () => mislukt(new Error('Het bestand kon niet gelezen worden.'));
    lezer.onload = () => {
      const tekst = typeof lezer.result === 'string' ? lezer.result : '';
      const komma = tekst.indexOf(',');
      klaar(komma === -1 ? tekst : tekst.slice(komma + 1));
    };
    lezer.readAsDataURL(bestand);
  });
}

async function verklein(bestand: File): Promise<Blob | null> {
  // Lukt het verkleinen niet — een oude browser, een raar bestand — dan gaat
  // het origineel eropuit. Beter een grote upload dan geen upload.
  if (typeof createImageBitmap !== 'function') return null;

  let beeld: ImageBitmap;
  try {
    beeld = await createImageBitmap(bestand);
  } catch {
    return null;
  }

  const grootste = Math.max(beeld.width, beeld.height);
  const schaal = grootste > MAXIMALE_ZIJDE ? MAXIMALE_ZIJDE / grootste : 1;
  const doek = document.createElement('canvas');
  // Pixels, geen centen — vandaar dat afronden hier wel mag.
  /* eslint-disable no-restricted-properties */
  doek.width = Math.round(beeld.width * schaal);
  doek.height = Math.round(beeld.height * schaal);
  /* eslint-enable no-restricted-properties */

  const tekenaar = doek.getContext('2d');
  if (tekenaar === null) {
    beeld.close();
    return null;
  }
  tekenaar.drawImage(beeld, 0, 0, doek.width, doek.height);
  beeld.close();

  return new Promise((klaar) => {
    doek.toBlob((blob) => klaar(blob), 'image/jpeg', 0.85);
  });
}

export async function maakKlaarVoorInlezen(bestand: File): Promise<Klaargemaakt> {
  if (bestand.type === 'application/pdf') {
    return { data: await naarBase64(bestand), mediaType: 'application/pdf' };
  }

  const kleiner = await verklein(bestand);
  if (kleiner === null) {
    return { data: await naarBase64(bestand), mediaType: bestand.type };
  }
  return { data: await naarBase64(kleiner), mediaType: 'image/jpeg' };
}
